import React from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import chcoPdf from "../assets/chco-acknowledgement.pdf";
import acknowledgement from "../assets/Acknowledgement-Form-Signature-Page-Spanish.pdf";

const styles = {
  container: {
    color: "#5B5B5B",
    marginBottom: "1rem",
    fontSize: "1rem",
    fontFamily: '"Roboto", sans-serif'
  },
  titleMain: {
    color: "#5B5B5B",
    fontSize: "1.5rem",
    textTransform: "uppercase",
    fontFamily: "ProximaSoft-Semibold, sans-serif"
  },
  titleSub: {
    marginTop: "1rem",
    textAlign: "left",
    color: "#5B5B5B",
    fontSize: "1.5rem"
  },
  paragraph: {
    fontFamily: '"Roboto", sans-serif',
    lineHeight: "normal",
    color: "#5B5B5B"
  },
  list: {
    marginLeft: "2rem"
  },
  listItem: {
    marginTop: "1rem",
    paddingLeft: "0.5rem",
    color: "#5B5B5B",
    fontSize: "1rem",
    lineHeight: "normal",
    fontFamily: '"Roboto", sans-serif'
  },
  underline: {
    margin: "1rem 0",
    textDecoration: "underline",
    fontFamily: '"Roboto", sans-serif'
  },
  consentMessage: {
    marginTop: "1rem",
    fontFamily: '"Roboto", sans-serif',
    color: "5B5B5B",
    fontSize: "1rem"
  },
  link: {
    display: "block",
    color: "rgb(0, 0, 238)",
    textDecoration: "none",
    fontFamily: "ProximaSoft-Semibold,sans-serif!important"
  },
  dialogContent: {
    height: "80vh"
  },
  iframe: {
    border: "none",
    width: "100%",
    height: "100%"
  }
};

const Consent = () => {
  const [openPdfDialog, setOpenPdfDialog] = React.useState(false);
  const [pdfUrl, setPdfUrl] = React.useState("");

  const handleOpenPdf = (e, url) => {
    e.preventDefault();
    setPdfUrl(url);
    setOpenPdfDialog(true);
  };

  const isMobile = () => {
    if (typeof navigator === "undefined") return false;
    return /Mobi|Android/i.test(navigator.userAgent);
  };

  return (
    <div style={styles.container}>
      <h1 className="main title" style={styles.titleMain}>
        Consent For Telehealth Services And Treatment
      </h1>

      <h2 className="title" style={styles.titleSub}>
        To our patients and families:
      </h2>

      <p style={styles.paragraph}>
        Thank you for choosing us for your telehealth care. Patients and
        families are essential participants in health care and we want you to
        understand your rights and responsibilities while receiving care from
        us. If you have any questions about this form, please ask your provider.
        If you are a parent/legally-authorized representative of a child, please
        read this agreement with the understanding that &ldquo;I&rdquo; and
        &ldquo;me&rdquo; means the child.
      </p>

      <ol>
        {/* 1 */}
        <li style={styles.listItem}>
          <p style={styles.underline}>Consent for Treatment:</p>
          I consent to telehealth care performed by my physician and all other
          associated health care providers at the University of Colorado School
          of Medicine (&ldquo;CUSOM&rdquo;) and/ or Children&rsquo;s Hospital
          Colorado (&ldquo;CHCO&rdquo;) (the &ldquo;Providers&rdquo;). This
          includes examinations, diagnostic testing, treatment, and other health
          care services deemed medically necessary in the Providers&rsquo;
          professional judgment. I understand that the practice of medicine and
          surgery is not an exact science and that diagnosis and treatment may
          cause injury or even death. I also understand that I have the option
          to refuse the delivery of health care services by telehealth at any
          time without affecting my right to future care or treatment, and
          without risking the loss or withdrawal of any benefits to which I
          would otherwise be entitled. If I am pregnant, this consent also
          applies to my fetus.
        </li>

        {/* 2 */}
        <li style={styles.listItem}>
          <p style={styles.underline}>Consent for Telehealth Services:</p>
          Telehealth involves transmission of video, photographs, and/or details
          of my medical record such as x-rays and test results (collectively,
          &ldquo;Data&rdquo;). All Data is sent by secure electronic means to
          the Providers to facilitate the medical service being performed. I
          understand that:
          <ul>
            <li style={styles.listItem}>
              I will be informed of any other people who are present at either
              end of the telehealth encounter, and have the right to exclude
              anyone from either location.
            </li>
            <li style={styles.listItem}>
              All confidentiality protections required by law or regulation will
              apply to my care.
            </li>
            <li style={styles.listItem}>
              I have the right to refuse or stop participation in telehealth
              services at any time and request alternate services such as an
              in-person appointment. However, I understand that equivalent
              in-person services might not be available at the same location or
              time as telehealth services.
            </li>
            <li style={styles.listItem}>
              If I do not want to receive health care services by telehealth, it
              will not affect my right to future care or treatment, or any
              insurance/ program benefits to which I would otherwise be
              entitled.
            </li>
            <li style={styles.listItem}>
              If an emergency occurs during a telehealth encounter at a hospital
              or clinic, health care personnel at my location will manage the
              emergency. If an emergency occurs during a telehealth encounter
              when I am at a non-health-care site, I should call 911 and stay on
              the video connection (if applicable) until help arrives.
            </li>
          </ul>
        </li>

        {/* 3 */}
        <li style={styles.listItem}>
          <p style={styles.underline}>Records and Release of Information:</p>
          Transmitted Data may become part of my medical record. Data will not
          be transmitted to people outside my health care team except as
          described below, and/or if I provide additional written consent.
          <ul>
            <li style={styles.listItem}>
              I will have access to all of the information in my medical record
              resulting from the telehealth services that I would have for a
              similar in-person visit, as provided by federal and state law.
            </li>
            <li style={styles.listItem}>
              The Providers may use or disclose my health information for
              treatment, continuity of care, payment, or internal operations, or
              when required by law or regulation in certain unique situations.
            </li>
            <li style={styles.listItem}>
              All releases of information are subject to the same laws and
              regulations as in-person care. If I am participating in a human
              subject research protocol, my medical information may also be
              released as described in the research consent form(s).
            </li>
          </ul>
        </li>

        {/* 4 */}
        <li style={styles.listItem}>
          <p style={styles.underline}>
            Payment Agreement/ Assignment of Benefits:
          </p>
          I agree to be responsible for any co-payments, deductibles, or other
          charges from the Providers and their providers that are not covered or
          paid by insurance or other third party payors &ndash; except as
          prohibited by any state or federal law, or any agreement between my
          insurance company and the Providers or University of Colorado Medicine
          (Faculty Practice Plan (&ldquo;CU Medicine&rdquo;)). I authorize the
          Providers and CU Medicine to file any claims for payment of any
          portion of the patient bills, and assign all rights and benefits
          payable for health care services to the provider or organization
          furnishing the services. I agree, subject to state and federal law, to
          pay all costs, attorney fees, expenses, delinquent charges, and
          interest in the event the Providers and/or CU Medicine have to take
          action to collect the same because of my failure to pay all incurred
          charges in full. It is my responsibility to know what providers and
          telehealth services are covered under my insurance plan. I understand
          that I may be billed and agree to pay all bills submitted by the
          Providers, CU Medicine, and/or other providers involved with the
          provision of telehealth services.
        </li>

        {/* 5 */}
        <li style={styles.listItem}>
          <p style={styles.underline}>
            Consent to be Contacted (Telephone Consumer Protection Act):
          </p>
          By providing a telephone number (landline or cellular) or other
          wireless device, I agree that in order for the Providers, CU Medicine,
          and/or other providers involved with the provision of telehealth
          services to service my account(s) (including contacting me about
          appointment reminders, surveys, obtaining potential financial
          assistance for my account(s)), or to collect any amounts I may owe,
          the Providers, CU Medicine, and/or other providers involved with the
          provision of telehealth services may contact me at the telephone
          number(s) provided which could result in charges to me. I expressly
          consent that methods of contact may include SMS text messages, phone
          calls, including automated technology such as an auto-dialing device,
          pre- recorded messages, and artificial voice messages as applicable.
          This consent applies to all services and billing associated with my
          account(s) and is not a condition of purchasing services.
        </li>

        {/* 6 */}
        <li style={styles.listItem}>
          I consent to being contacted by SMS (short message service) text
          message and automatically-dialed appointment reminders.
        </li>
      </ol>

      <p className="consent-message" style={styles.consentMessage}>
        By using this website, I acknowledge that I have read this information
        and agree to treatment by telehealth.
      </p>

      <div>
        {isMobile() ? (
          <a href={chcoPdf} download style={styles.link}>
            Download CHCO acknowledgement PDF
          </a>
        ) : (
          <a
            href={chcoPdf}
            onClick={e => handleOpenPdf(e, chcoPdf)}
            style={styles.link}
          >
            View CHCO acknowledgement PDF
          </a>
        )}
      </div>

      <div>
        {isMobile() ? (
          <a href={acknowledgement} download style={styles.link}>
            Download Acknowledgement Form Signature Page Spanish PDF
          </a>
        ) : (
          <a
            href={acknowledgement}
            onClick={e => handleOpenPdf(e, acknowledgement)}
            style={styles.link}
          >
            View Acknowledgement Form Signature Page Spanish PDF
          </a>
        )}
      </div>

      <Dialog
        open={openPdfDialog}
        onClose={() => setOpenPdfDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent style={styles.dialogContent}>
          <iframe src={pdfUrl} title="PDF Viewer" style={styles.iframe} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Consent;
